/**
 * API共通エラーハンドリング
 */

const RATE_LIMIT_MESSAGE = 'リクエストが集中しています。しばらくしてから再度お試しください。';

/**
 * APIレスポンスのステータスコードを検査し、429の場合はユーザーフレンドリーなエラーをスローする。
 * 429以外の場合は何もしない（呼び出し元で個別処理を継続）。
 */
export function throwIfRateLimited(response: Response): void {
  if (response.status === 429) {
    throw new Error(RATE_LIMIT_MESSAGE);
  }
}

/**
 * バックエンドのエラーレスポンスから errorCode を抽出する。
 * `details.errorCode` を最優先で読み、無ければ `error`/`details` のフラットフィールドを許容する。
 */
export function extractErrorCode(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') return undefined;
  const obj = body as Record<string, unknown>;
  const details = obj.details;
  if (details && typeof details === 'object') {
    const code = (details as Record<string, unknown>).errorCode;
    if (typeof code === 'string') return code;
  }
  const directCode = obj.errorCode;
  if (typeof directCode === 'string') return directCode;
  return undefined;
}

/**
 * errorCode を保持するアプリケーションエラー。
 * 文字列マッチに頼らず、ハンドラ側で `instanceof ApiError` + `errorCode` を見て分岐できる。
 */
export class ApiError extends Error {
  status: number | null;
  errorCode?: string;
  details?: unknown;

  constructor(
    message: string,
    options: { status?: number | null; errorCode?: string; details?: unknown } = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = options.status ?? null;
    this.errorCode = options.errorCode;
    this.details = options.details;
  }
}

/**
 * Backend が返す英語メッセージをユーザー向けの日本語に変換するためのフォールバックマップ。
 *
 * Backend 側の段階的な日本語化が完了するまでの暫定マップ。完全一致 → サブ文字列マッチの順で評価する。
 */
const ERROR_MESSAGE_MAP: Array<{ pattern: string | RegExp; message: string }> = [
  { pattern: /^Event not found$/i, message: 'イベントが見つかりませんでした' },
  { pattern: /^Event is required$/i, message: 'イベントが指定されていません' },
  { pattern: /^Missing eventId$/i, message: 'イベントIDが指定されていません' },
  { pattern: /^Invalid event id$/i, message: 'イベントIDの形式が正しくありません' },
  { pattern: /^Participant not found$/i, message: '参加者が見つかりませんでした' },
  { pattern: /^Comment not found$/i, message: 'コメントが見つかりませんでした' },
  { pattern: /^Tool not found$/i, message: 'ツールが見つかりませんでした' },
  { pattern: /^User not found$/i, message: 'ユーザーが見つかりませんでした' },
  { pattern: /^Invalid JSON body$/i, message: 'リクエストの形式が正しくありません' },
  { pattern: /^Invalid request body$/i, message: 'リクエストの形式が正しくありません' },
  { pattern: /^Authentication failed$/i, message: '認証に失敗しました。再度ログインしてください' },
  { pattern: /^Authentication required$/i, message: 'ログインが必要です' },
  { pattern: /^Forbidden$/i, message: 'この操作を行う権限がありません' },
  { pattern: /^Permission denied$/i, message: 'この操作を行う権限がありません' },
  { pattern: /^Validation error$/i, message: '入力内容に誤りがあります' },
  { pattern: /^Maximum participants reached$/i, message: '参加者数が上限に達しています' },
  { pattern: /^Event is cancelled$/i, message: 'このイベントは中止されています' },
  { pattern: /^The response deadline for this event has passed$/i, message: '回答の受付は締め切られました' },
  { pattern: /^This event is no longer accepting participants$/i, message: 'このイベントは参加受付を終了しています' },
  { pattern: /^This event is not published yet$/i, message: 'このイベントはまだ公開されていません' },
  { pattern: /^This event does not allow guest participation$/i, message: 'このイベントはゲスト参加を受け付けていません' },
  { pattern: /^Event is not published$/i, message: 'このイベントはまだ公開されていません' },
  { pattern: /^Guest participation is not allowed$/i, message: 'このイベントはゲスト参加を受け付けていません' },
  { pattern: /^Only organizer/i, message: 'この操作は主催者のみ行えます' },
  { pattern: /^Only collaborators?/i, message: 'この操作は共同編集者のみ行えます' },
  { pattern: /Only organizer or collaborators/i, message: 'この情報は主催者または共同編集者のみが閲覧できます' },
  { pattern: /^Invite not found$/i, message: '招待リンクが見つかりませんでした' },
  { pattern: /^Invite expired$/i, message: '招待リンクの有効期限が切れています' },
  { pattern: /^Invite already used$/i, message: 'この招待リンクはすでに使われています' },
  { pattern: /^Collaborator limit reached$/i, message: '共同編集者の上限に達しています' },
  { pattern: /^Already collaborator$/i, message: 'すでに共同編集者として登録されています' },
  { pattern: /^Subscription is required$/i, message: '有料プランへのアップグレードが必要です' },
  { pattern: /^Stripe (.+) failed$/i, message: '決済処理に失敗しました。時間をおいて再度お試しください' },
  { pattern: /^Internal server error$/i, message: 'サーバーエラーが発生しました。時間をおいて再度お試しください' },
  { pattern: /HTTP \d+: /, message: '通信エラーが発生しました。時間をおいて再度お試しください' },
];

/**
 * Backend からの英語エラーメッセージを日本語にフォールバック変換する。
 * 既に日本語のメッセージはそのまま返す。
 */
export function translateApiError(message: string | null | undefined, fallback?: string): string {
  if (!message) return fallback ?? 'エラーが発生しました';
  // 日本語が含まれていれば backend が既に日本語化していると判断
  if (/[ぁ-んァ-ン一-龯]/.test(message)) return message;
  for (const { pattern, message: jp } of ERROR_MESSAGE_MAP) {
    if (typeof pattern === 'string') {
      if (message === pattern) return jp;
    } else if (pattern.test(message)) {
      return jp;
    }
  }
  // マッチしない英語メッセージはユーザーに表示せず、汎用日本語を返す
  return fallback ?? 'エラーが発生しました。時間をおいて再度お試しください';
}
